/**
 * Copy, cut, drag, paste and drop (table C in the part 09 log; one test per row; decision 66: the writer never sees
 * escapes). Other apps get the text as the editor shows it untouched; inside the app, a paste or drop of what the page
 * last gave out brings back its source, so moving or copying text keeps its formatting exactly (deep-fix r2, r3, and
 * the user's choice after r6).
 */
import type { EditorState, Extension, Text } from '@codemirror/state';
import { EditorView } from '@codemirror/view';
import type { Tree } from '@lezer/common';
import { chapterField } from './chapter-state.ts';
import { liveDecorations } from './live.ts';
import type { SourceRange } from './live.ts';
import { unitsOf } from './units.ts';

/**
 * The text of `from`–`to` as the editor shows it untouched (C1–C4, C8, C9): escapes and entities as their characters,
 * hidden markup left out (the part of it inside the range, when the range cuts it), a widget as its text when the range
 * meets it. Pure; costs what the range holds.
 */
export function visibleText(tree: Tree, doc: Text, from: number, to: number): string {
  const cuts = liveDecorations(tree, doc, { from, to }, [])
    .decorations.flatMap((d) => ((d.kind === 'hide' || d.kind === 'widget') && d.from < to && d.to > from ? [d] : []))
    .sort((a, b) => a.from - b.from);
  let text = '';
  let at = from;
  for (const cut of cuts) {
    text += doc.sliceString(at, Math.max(at, cut.from));
    if (cut.kind === 'widget') text += cut.text;
    at = Math.max(at, Math.min(cut.to, to));
  }
  return text + doc.sliceString(at, to);
}

/** A copy a paste or drop brought back: its source, what it showed, and its text units as the chapter read them. */
export interface Recalled {
  readonly source: string;
  readonly shown: string;
  readonly text: string;
}

/** The last copy, cut or drag from the editor: what other apps got, and the source it came from. */
interface Copied {
  readonly visible: string;
  readonly source: string;
  /** The text units of the source as the chapter read them, in order (A5, *deep-fix r9*). */
  readonly text: string;
}

/**
 * What the page remembers of the last copy, cut or drag (C10–C12, C16). It lives in the page, never on the clipboard,
 * so no other site can put source into a paste: a pasted or dropped text is taken as source only when it is exactly what
 * this page last gave out.
 */
export class ClipboardMemory {
  private last: Copied | null = null;
  /**
   * The copy the last paste or drop brought back, or null when it brought back none (table X, X4; A5): its source, what
   * it showed, and its text as the chapter read it (*deep-fix r9*).
   */
  recalled: Recalled | null = null;

  /** Keeps the last copy; `text` is its text units as the chapter read them (by default, its visible text's characters). */
  remember(visible: string, source: string, text: string = visible.replaceAll('\n', '')): void {
    this.last = { visible, source, text };
  }

  /** The last copy when `text` is what it gave out, else null. */
  recall(text: string): Recalled | null {
    return this.last !== null && this.last.visible === text ? { source: this.last.source, shown: this.last.visible, text: this.last.text } : null;
  }
}

/** The page's memory, shared by every editor it opens, so a copy in one chapter pastes into another (C12). */
export const pageClipboard: ClipboardMemory = new ClipboardMemory();

/**
 * The ranges CodeMirror copies, cuts or drags: the selected ones, or, with nothing selected, each cursor's whole line
 * once (C10, C11): the same choice as its own `copiedRange`, which the filter can't see.
 */
export function copiedRanges(state: EditorState): SourceRange[] {
  const selected = state.selection.ranges.filter((range) => !range.empty);
  if (selected.length > 0) return selected.map(({ from, to }) => ({ from, to }));
  const lines: SourceRange[] = [];
  let upto = -1;
  for (const { from } of state.selection.ranges) {
    const line = state.doc.lineAt(from);
    if (line.number > upto) lines.push({ from: line.from, to: line.to });
    upto = line.number;
  }
  return lines;
}

/**
 * What a copy, cut or drag gives out (C10, C11, C14): the visible text of the ranges it takes, remembered with their
 * source; or `text` as it is when it isn't the source of those ranges (a path the filter can't follow).
 */
export function clipboardOut(memory: ClipboardMemory, text: string, state: EditorState): string {
  const ranges = copiedRanges(state);
  const source = ranges.map(({ from, to }) => state.sliceDoc(from, to)).join(state.lineBreak);
  if (source !== text) return text;
  const parsed = state.field(chapterField);
  const visible = ranges.map(({ from, to }) => visibleText(parsed.tree, state.doc, from, to)).join(state.lineBreak);
  // A5: which characters were text, so a paste can tell when the fragment read alone makes text markup or markup text.
  const units = unitsOf(parsed);
  const read = ranges.map(({ from, to }) => units.filter((u) => u.kind === 'text' && u.from >= from && u.to <= to).map((u) => u.show).join('')).join('');
  memory.remember(visible, source, read);
  return visible;
}

/** What a paste or drop puts in (C12, C13): the remembered source of this page's last copy, or `text` as it is. */
export function clipboardIn(memory: ClipboardMemory, text: string): string {
  memory.recalled = memory.recall(text);
  return memory.recalled?.source ?? text;
}

/** Table C in the editor: copy, cut and drag give out the visible text; paste and drop of it bring back the source. */
export function liveClipboard(memory: ClipboardMemory = pageClipboard): Extension {
  return [
    EditorView.clipboardOutputFilter.of((text, state) => clipboardOut(memory, text, state)),
    EditorView.clipboardInputFilter.of((text) => clipboardIn(memory, text)),
  ];
}
